import { Languages } from '@chiroup/core/enums/Languages.enum';
import { prescription } from '@chiroup/core/functions/createPrescription';
import { stripHtmlTags } from '@chiroup/core/functions/stripHtmlTags';
import { Exercise } from '@chiroup/core/types/Exercise.type';
import { ClockIcon } from '@heroicons/react/24/outline';
import { ChevronRightIcon } from '@heroicons/react/24/solid';
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { hcVerbiage } from '../../common/verbiage';
import { usePatientPortal } from '../../contexts/PatientPortalContext';
import FallbackImage from '../common/FallbackImage';

const ExerciseCard: React.FC<{
  exercise: Exercise;
  primaryColor?: string;
  basePath: string;
  language: string;
  isUpcoming?: boolean;
}> = ({ exercise, primaryColor = '#77bd43', basePath, language, isUpcoming }) => {
  const pill = useMemo(() => {
    const hasPrescription =
      exercise.prescriptionSets || exercise.prescriptionReps || exercise.prescriptionScheduleTimes;
    if (!hasPrescription) return null;

    return prescription(exercise, language === 'es' ? Languages.es : Languages.en);
  }, [exercise, language]);

  const description = stripHtmlTags(exercise.descr);

  return (
    <Link
      to={`${basePath}/exercises/${exercise.ID}`}
      className={`portal-card flex items-center gap-3 p-3.5 active:scale-[0.98] transition-transform${isUpcoming ? ' opacity-50' : ''}`}
    >
      <FallbackImage
        src={
          exercise.img ? `${exercise.img}${exercise.img.includes('?') ? '&' : '?'}w=96&q=80` : null
        }
        alt={exercise.name}
        imgClassName="shrink-0 w-12 h-12 rounded-xl object-cover"
        fallback={
          <div
            className="shrink-0 w-12 h-12 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: `${primaryColor}15` }}
          >
            <svg
              className="h-6 w-6"
              style={{ color: primaryColor }}
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
        }
      />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
          {exercise.name}
        </p>
        <div className="flex items-center gap-2 mt-0.5">
          {description && (
            <p className="text-xs text-gray-400 dark:text-gray-500 truncate">{description}</p>
          )}
        </div>
        {pill && (
          <span
            className="mt-1 inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium"
            style={{ backgroundColor: `${primaryColor}15`, color: primaryColor }}
          >
            {pill}
          </span>
        )}
      </div>
      <ChevronRightIcon className="h-4 w-4 text-gray-300 dark:text-gray-600 shrink-0" />
    </Link>
  );
};

const Exercises: React.FC<{ basePath: string }> = ({ basePath }) => {
  const { selectedPatient, language } = usePatientPortal();

  const exercisesByStatus = useMemo(() => {
    return (selectedPatient?.exercises || []).reduce(
      (obj: { active: Exercise[]; inactive: Exercise[] }, exercise: Exercise) => {
        obj[exercise.phase === 2 ? 'inactive' : 'active'].push(exercise);

        return obj;
      },
      { active: [] as Exercise[], inactive: [] as Exercise[] }
    );
  }, [selectedPatient?.exercises]);

  const primaryColor = selectedPatient?.clinic?.primaryColor || '#77bd43';

  if (!selectedPatient?.exercises?.length) {
    return (
      <div className="text-center py-16">
        <p className="text-sm text-gray-400 dark:text-gray-500">
          {hcVerbiage.noExercises[language]}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          {hcVerbiage.exercises[language]}
        </h2>
        <p className="mt-1 text-sm text-gray-400 dark:text-gray-500">
          {hcVerbiage.exercisesDescription[language]}
        </p>
      </div>

      {/* Active exercises */}
      {!!exercisesByStatus.active.length && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2.5">
          {exercisesByStatus.active.map((exercise: Exercise) => (
            <ExerciseCard
              key={exercise.ID}
              exercise={exercise}
              primaryColor={primaryColor}
              basePath={basePath}
              language={language}
            />
          ))}
        </div>
      )}

      {/* Upcoming section */}
      {!!exercisesByStatus.inactive?.length && (
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2 px-1">
            <ClockIcon
              className="h-4 w-4 text-gray-300 dark:text-gray-600 shrink-0"
              aria-hidden="true"
            />
            <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
              Upcoming
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2.5">
            {exercisesByStatus.inactive.map((exercise: Exercise) => (
              <ExerciseCard
                key={exercise.ID}
                exercise={exercise}
                primaryColor={primaryColor}
                basePath={basePath}
                language={language}
                isUpcoming
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Exercises;
